import axios from 'axios';
import { useMemo, useRef } from 'react';
import { useApi } from '@dreamtree-org/twreact-ui';
import apiMap from '@api';
import { getApiRoot } from '@/lib/domainResolver';
import { isMultiTenant, tenantHeaders } from '@/lib/tenantAuth';

/**
 * The company the top-right switcher currently has selected, kept at module scope and
 * read by an interceptor at REQUEST time rather than baked into the axios instance.
 * createRequestInstance runs once per useApi consumer, so an instance created before the
 * user switched company would otherwise keep sending the old id forever.
 *
 * Purely a hint: services/rbac/identity.js validates it against the user's memberships
 * and ignores anything they aren't entitled to.
 */
let activeCompanyId = null;

export function setActiveCompanyId(id) {
  activeCompanyId = id == null ? null : String(id);
}

/**
 * App-configured wrapper around twreact-ui's useApi. baseURL is resolved at startup from
 * config/domain.resolver.js by matching this deployment's origin (domainResolver.js) —
 * empty for every same-origin deployment (Vite proxies /api to the backend in dev), so
 * relative "/api/..." paths are unchanged; a cross-origin deployment would prepend its
 * API origin. withCredentials so the httpOnly JWT cookie is sent.
 * Returns { data, error, loading, fetchApi, fetchData }.
 */
export function useAppApi() {
  return useApi({
    apiMap,
    createRequestInstance: () => {
      const instance = axios.create({ baseURL: getApiRoot(), withCredentials: true });
      instance.interceptors.request.use((config) => {
        // Multi-tenant mode: the session cookie names the company; requests carry the CSRF
        // token and the active branch instead of a client-chosen company id.
        if (isMultiTenant()) Object.assign(config.headers, tenantHeaders(config.method));
        else if (activeCompanyId) config.headers['X-Company-Id'] = activeCompanyId;
        return config;
      });
      return instance;
    },
  });
}

/**
 * The api object with a STABLE identity — use this in components.
 *
 * twreact-ui's useApi rebuilds fetchApi/fetchData as plain per-render functions, so
 * any component listing fetchApi in an effect/useCallback deps array refetches EVERY
 * render (the /app-workflow/:id request storm → ERR_INSUFFICIENT_RESOURCES). Components
 * must never put the api object in a deps array — this hook is the way to guarantee
 * that: route calls through a ref so the closure is always fresh while the identity
 * never changes. lib/apiDepConvention.test.js fails the build if fetchApi/fetchData
 * ever appear in a deps array anywhere under client/src.
 */
export function useStableApi() {
  const api = useAppApi();
  const latest = useRef(api);
  latest.current = api;
  return useMemo(() => ({
    fetchApi: (...args) => latest.current.fetchApi(...args),
    fetchData: (...args) => latest.current.fetchData(...args),
  }), []);
}
